import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { adminApi, AdminUser } from '../../utils/api'
import {
  Users,
  Mail,
  Calendar,
  Plus,
  Trash2,
  Shield,
  Eye,
  AlertCircle,
  X,
} from 'lucide-react'

export default function AdminUsersPage() {
  const { token, user: currentUser } = useAuth()
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create user modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newRole, setNewRole] = useState<'admin' | 'reviewer'>('reviewer')
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [token])

  async function loadUsers() {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const response = await adminApi.getUsers(token)
      setUsers(response?.items || [])
    } catch (err) {
      console.error('[AdminUsers] Failed to load:', err)
      setError('Failed to load users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return

    setCreateError(null)

    if (newPassword.length < 12) {
      setCreateError('Password must be at least 12 characters')
      return
    }

    setCreateLoading(true)
    try {
      await adminApi.createUser(token, {
        email: newEmail,
        password: newPassword,
        role: newRole,
      })
      setShowCreateModal(false)
      setNewEmail('')
      setNewPassword('')
      setNewRole('reviewer')
      await loadUsers()
    } catch (err: unknown) {
      const apiErr = err as { message?: string }
      setCreateError(apiErr?.message || 'Failed to create user')
    } finally {
      setCreateLoading(false)
    }
  }

  async function handleDeleteUser(userId: string) {
    if (!token) return

    setDeleteLoading(true)
    try {
      await adminApi.deleteUser(token, userId)
      setDeleteConfirm(null)
      await loadUsers()
    } catch (err: unknown) {
      const apiErr = err as { message?: string }
      setError(apiErr?.message || 'Failed to delete user')
      setDeleteConfirm(null)
    } finally {
      setDeleteLoading(false)
    }
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return (
          <span className="badge bg-purple-100 text-purple-800 inline-flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Admin
          </span>
        )
      case 'reviewer':
        return (
          <span className="badge bg-blue-100 text-blue-800 inline-flex items-center gap-1">
            <Eye className="w-3 h-3" />
            Reviewer
          </span>
        )
      default:
        return <span className="badge bg-gray-100 text-gray-600">User</span>
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 mt-1">Manage admin and reviewer accounts</p>
        </div>
        <button
          onClick={() => {
            setShowCreateModal(true)
            setCreateError(null)
          }}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-gray-500">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900">
                              {user.email}
                            </span>
                          </div>
                          <span className="text-xs text-gray-400">
                            {user.user_id}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {deleteConfirm === user.user_id ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-red-600">Confirm?</span>
                          <button
                            onClick={() => handleDeleteUser(user.user_id)}
                            disabled={deleteLoading}
                            className="btn text-xs px-3 py-1 text-white bg-red-600 hover:bg-red-700"
                          >
                            {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="btn text-xs px-3 py-1 text-gray-600 border border-gray-200"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(user.user_id)}
                          disabled={user.user_id === currentUser?.id}
                          title={
                            user.user_id === currentUser?.id
                              ? 'Cannot delete your own account'
                              : 'Delete user'
                          }
                          className="btn text-xs px-3 py-1 text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Add New User
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Create an admin or reviewer account. Use a strong password (min 12
              characters).
            </p>

            {createError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setNewRole('reviewer')}
                    className={`p-3 border rounded-lg flex items-center justify-center gap-2 text-sm transition-colors ${
                      newRole === 'reviewer'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    Reviewer
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewRole('admin')}
                    className={`p-3 border rounded-lg flex items-center justify-center gap-2 text-sm transition-colors ${
                      newRole === 'admin'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="Minimum 12 characters"
                  required
                  minLength={12}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Must be at least 12 characters. Share securely with the user.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createLoading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
